import React, { useState } from "react";
import { Dropdown } from "semantic-ui-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toPng } from "html-to-image";

// ── Helpers de formato ────────────────────────────────────────────────────────

const getNeededDecimals = (value) => {
  if (typeof value !== "number" || isNaN(value) || value === 0) return 2;
  const abs = Math.abs(value);
  if (abs < 0.0000005) return -1;
  if (abs >= 1) return 2;
  const leadingZeros = Math.floor(-Math.log10(abs));
  return Math.max(2, leadingZeros + 2);
};

const getRowDecimalPlaces = (values) => {
  let maxDecimals = 2;
  for (const v of values) {
    if (typeof v !== "number" || isNaN(v) || v === 0) continue;
    const needed = getNeededDecimals(v);
    if (needed === -1) continue;
    if (needed > maxDecimals) maxDecimals = needed;
  }
  return Math.min(maxDecimals, 6);
};

const formatVal = (value, decimals) => {
  if (typeof value !== "number" || isNaN(value) || value === 0) return "-";
  if (Math.abs(value) < 0.0000005) return (0).toFixed(decimals);
  return value.toFixed(decimals);
};

const getPillColor = (val, prevVal) => {
  if (val == null || val === 0) return null;
  if (prevVal == null || prevVal === 0) return [208, 208, 208];
  if (val > prevVal) return [244, 168, 164];
  if (val < prevVal) return [168, 213, 181];
  return [208, 208, 208];
};

// ── Componente ────────────────────────────────────────────────────────────────

const KPIExportIconButton = ({
  title = "KPI",
  conceptos = [],
  totales = {},
  years = [],
  codes = {},
  t = (k) => k,
  chartRefs = [],
  sectionGroups = null, // [{ label, items }] para tablas con secciones (ej. waste)
  methodColumn = null,  // { header: string, getValue: (item) => string } columna extra entre indicador y años
  siteName = "",
  // Hook opcional para dibujar gráficos como SVG vectorial (en vez de
  // rasterizar un ref con toPng) después de procesar chartRefs — usado por
  // Energía/Combustibles para sus gráficos DualAxes. Si no se pasa, el
  // comportamiento es exactamente igual al de siempre (cero impacto para
  // los demás dashboards que usan este componente).
  renderCustomCharts = null, // async (doc, {margin, pageWidth, pageHeight, imgWidth, yOffset}) => newYOffset
  // Hook opcional para formatear el valor de cada celda año según
  // AJUSTES DE MAGNITUDES NUMÉRICAS (ej. Social pasa una versión que
  // replica la inferencia de regla por fila que ya usa su tabla en
  // pantalla). Si no se pasa, se mantiene el formateo de siempre
  // (toFixed(2) + coma) — cero impacto para los dashboards que todavía
  // no lo usan.
  formatCellValue = null, // (value: number, item: string, unit: string) => string
}) => {
  const [loading, setLoading] = useState(false);

  const generatePDF = async (includeCharts = false) => {
    setLoading(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yOffset = 36;

      // ── Encabezado ──────────────────────────────────────────────────────────
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.setTextColor(20, 40, 80);
      doc.setCharSpace(1.5);
      doc.text(`Reporte: ${title}`, margin, yOffset);
      doc.setCharSpace(0);
      yOffset += 16;

      if (siteName) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.setTextColor(60, 80, 120);
        doc.text(siteName.toLocaleUpperCase(), margin, yOffset);
        yOffset += 14;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.setTextColor(130, 130, 130);
      doc.setCharSpace(0.8);
      doc.text(
        `Generado el ${new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })}`,
        margin,
        yOffset
      );
      doc.setCharSpace(0);
      yOffset += 18;

      doc.setDrawColor(210, 210, 210);
      doc.line(margin, yOffset, pageWidth - margin, yOffset);
      yOffset += 12;

      // ── Gráficos opcionales ──────────────────────────────────────────────────
      const imgWidth = pageWidth - margin * 2;

      const captureElement = async (ref) => {
        if (!ref?.current) return null;
        try {
          return await toPng(ref.current, {
            pixelRatio: 2,
            filter: (node) => node.dataset?.pdfExclude !== "true",
          });
        } catch {
          return null;
        }
      };

      const addImageToPDF = (img) => {
        if (!img) return;
        const imgProps = doc.getImageProperties(img);
        const naturalH = (imgProps.height * imgWidth) / imgProps.width;
        const maxH = pageHeight - margin * 2;

        // Nunca se achica el ancho -- si el gráfico solo (a ancho completo)
        // ya es más alto que una página entera, se reduce proporcionalmente
        // (único caso donde no hay forma de evitarlo sin cortar contenido).
        let drawWidth = imgWidth;
        let drawHeight = naturalH;
        if (naturalH > maxH) {
          drawHeight = maxH;
          drawWidth = imgWidth * (maxH / naturalH);
        }

        // Si no entra en lo que queda de la página actual, pasar a una
        // nueva en vez de achicarlo más para que "entre" -- eso era lo que
        // dejaba espacio en blanco a la derecha en el segundo gráfico.
        const remaining = pageHeight - margin - yOffset;
        if (drawHeight > remaining) {
          doc.addPage();
          yOffset = margin;
        }

        doc.addImage(img, "PNG", margin, yOffset, drawWidth, drawHeight);
        yOffset += drawHeight + 10;
      };

      if (includeCharts && chartRefs.length > 0) {
        for (const { ref } of chartRefs) {
          const img = await captureElement(ref);
          if (img) addImageToPDF(img);
        }
      }

      if (includeCharts && renderCustomCharts) {
        yOffset = await renderCustomCharts(doc, { margin, pageWidth, pageHeight, imgWidth, yOffset });
      }

      // ── Tabla ────────────────────────────────────────────────────────────────
      if (conceptos.length === 0 || years.length === 0) {
        doc.save(`reporte_${new Date().getFullYear()}.pdf`);
        return;
      }

      if (yOffset + 60 > pageHeight - margin) { doc.addPage(); yOffset = margin; }

      const sortedYears = [...years].sort((a, b) => Number(b) - Number(a)).slice(0, 5);
      const yearKeys = sortedYears.map(String);

      // Measure methodColumn header at render size (8pt bold) before resetting font
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      const METHOD_COL_W = methodColumn
        ? Math.max(doc.getTextWidth(methodColumn.header) + methodColumn.header.length * 0.4 + 14, 55)
        : 0;
      doc.setFontSize(7);
      doc.setFont(undefined, "normal");

      const tableColumns = [
        { header: t("code") || "Código", dataKey: "code" },
        { header: t("indicators") || "Indicador", dataKey: "indicador" },
        ...(methodColumn ? [{ header: methodColumn.header, dataKey: "method" }] : []),
        ...sortedYears.map((y) => ({ header: String(y), dataKey: String(y) })),
        { header: t("units") || "Unidad", dataKey: "unidad" },
      ];

      // Extrae el valor numérico de una entrada — soporta estructura estándar { total }
      // y estructura GEI { scope1, scope2, scope3, emissions }
      const extractVal = (entry) => {
        if (entry == null) return null;
        if (typeof entry === "number") return entry;
        if (entry.total != null) return Number(entry.total);
        if (entry.scope1 != null || entry.scope2 != null || entry.scope3 != null)
          return (entry.scope1 || 0) + (entry.scope2 || 0) + (entry.scope3 || 0);
        if (entry.emissions != null) return Number(entry.emissions);
        return null;
      };

      const buildRawVals = (item) => {
        const vals = {};
        sortedYears.forEach((yr) => {
          const val = extractVal(totales[item]?.[yr]);
          vals[String(yr)] = val != null ? Number(val) : null;
        });
        return vals;
      };

      const buildRow = (item) => {
        const row = { code: codes[item] || "-", indicador: t(item) || item };
        if (methodColumn) row.method = methodColumn.getValue(item);
        const unit = sortedYears.map((yr) => totales[item]?.[yr]?.unit).find(Boolean) || "-";
        sortedYears.forEach((yr) => {
          const val = extractVal(totales[item]?.[yr]);
          row[String(yr)] = val != null
            ? (formatCellValue ? formatCellValue(Number(val), item, unit) : Number(val).toFixed(2).replace('.', ','))
            : "-";
        });
        row.unidad = unit;
        // Valores crudos para el coloreado por año, colgados directamente del
        // objeto fila (en vez de un array paralelo indexado por posición) —
        // jspdf-autotable devuelve este mismo objeto como data.row.raw en los
        // hooks, así que el lookup no depende de que data.row.index se
        // mantenga alineado con el array de filas.
        row.__rawVals = buildRawVals(item);
        return row;
      };

      // Con sectionGroups (ej. Residuos): armar la tabla en secciones, cada
      // una con una fila-encabezado que ocupa todo el ancho (misma agrupación
      // que ya se ve en pantalla).
      const tableRows = [];

      if (sectionGroups && sectionGroups.length > 0) {
        sectionGroups.forEach((section) => {
          if (!section.items || section.items.length === 0) return;
          tableRows.push({
            code: {
              content: section.label,
              colSpan: tableColumns.length,
              styles: {
                halign: "left",
                fillColor: [225, 230, 245],
                textColor: [20, 40, 80],
                fontStyle: "bold",
                fontSize: 7.5,
              },
            },
          });
          section.items.forEach((item) => {
            tableRows.push(buildRow(item));
          });
        });
      } else {
        conceptos.forEach((item) => {
          tableRows.push(buildRow(item));
        });
      }

      autoTable(doc, {
        startY: yOffset,
        columns: tableColumns,
        body: tableRows,
        styles: {
          fontSize: 7,
          cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
          halign: "center",
          valign: "middle",
          fillColor: [237, 235, 235],
          textColor: [40, 40, 40],
          lineColor: [255, 255, 255],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: [237, 237, 237],
          textColor: [237, 237, 237],
          fontStyle: "bold",
          fontSize: 6,
          lineWidth: 0,
          minCellHeight: 30,
          cellPadding: { top: 5, right: 3, bottom: 5, left: 3 },
        },
        columnStyles: {
          code: { halign: "left", cellWidth: 38 },
          indicador: { halign: "left" },
          ...(methodColumn ? { method: { halign: "left", cellWidth: METHOD_COL_W } } : {}),
        },
        theme: "plain",
        margin: { left: margin, right: margin },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          const yr = data.column.dataKey;
          if (!yearKeys.includes(yr)) return;
          const rowVals = data.row.raw?.__rawVals;
          if (!rowVals) return;
          const val = rowVals[yr];
          const yrIdx = sortedYears.findIndex((y) => String(y) === yr);
          const prevYr = yrIdx < sortedYears.length - 1 ? sortedYears[yrIdx + 1] : null;
          const prevVal = prevYr != null ? rowVals[String(prevYr)] : null;
          if (getPillColor(val, prevVal)) {
            data.cell.styles.textColor = [237, 235, 235];
          }
        },
        didDrawCell: (data) => {
          // Redibujar headers con charSpace
          if (data.section === "head") {
            const { x, y, width, height } = data.cell;
            const text = String(data.cell.raw || "");
            if (!text) return;
            doc.setFontSize(6);
            doc.setFont(undefined, "bold");
            doc.setTextColor(40, 40, 40);
            doc.setCharSpace(0.8);
            if (data.column.dataKey === "method") {
              const lines = doc.splitTextToSize(text, width - 6);
              const lineH = 4;
              const startY = y + height / 2 + 3 - ((lines.length - 1) * lineH) / 2;
              doc.text(lines, x + width / 2, startY, { align: "center" });
            } else {
              doc.text(text, x + width / 2, y + height / 2 + 3, { align: "center" });
            }
            doc.setCharSpace(0);
            return;
          }
          if (data.section !== "body") return;
          const yr = data.column.dataKey;
          if (!yearKeys.includes(yr)) return;
          const rowVals = data.row.raw?.__rawVals;
          if (!rowVals) return;
          const val = rowVals[yr];
          const yrIdx = sortedYears.findIndex((y) => String(y) === yr);
          const prevYr = yrIdx < sortedYears.length - 1 ? sortedYears[yrIdx + 1] : null;
          const prevVal = prevYr != null ? rowVals[String(prevYr)] : null;
          const color = getPillColor(val, prevVal);
          if (!color) return;

          const { x, y, width, height } = data.cell;
          const text = String(data.cell.raw || "-");
          const pillW = Math.min(width - 8, 58);
          const pillH = Math.max(height - 5, 10);
          const pillX = x + (width - pillW) / 2;
          const pillY = y + (height - pillH) / 2;

          doc.setFillColor(...color);
          doc.roundedRect(pillX, pillY, pillW, pillH, 3, 3, "F");

          doc.setFontSize(7);
          doc.setFont(undefined, "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(text, pillX + pillW / 2, pillY + pillH / 2 + 2.5, { align: "center" });
        },
      });

      const slug = title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, "_");
      const safeSite = siteName ? `_${siteName.replace(/\s+/g, "_")}` : "";
      doc.save(
        includeCharts
          ? `reporte_${slug}${safeSite}_con_graficos_${new Date().getFullYear()}.pdf`
          : `reporte_${slug}${safeSite}_${new Date().getFullYear()}.pdf`
      );
    } catch (err) {
      console.error("Error generando PDF:", err);
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { key: "sin", text: "Solo tabla",   icon: "table",     onClick: () => generatePDF(false) },
    { key: "con", text: "Con gráficos", icon: "chart bar", onClick: () => generatePDF(true) },
  ];

  return (
    <div data-pdf-exclude="true" style={{ display: "contents" }}>
      <Dropdown
        pointing="top right"
        icon={null}
        trigger={
          <button className="kpi-export-icon-btn" title="Descargar PDF" disabled={loading}>
            {loading
              ? <i className="spinner loading icon" style={{ color: "#555" }} />
              : <i className="download icon" style={{ color: "#555" }} />}
          </button>
        }
        options={options}
        disabled={loading}
      />
    </div>
  );
};

export default KPIExportIconButton;
